/**
 * AWS Amplify Configuration for Authentication
 * 
 * This file configures Amplify to work with Cognito for user authentication.
 * It supports both local development (mock auth) and production (Cognito).
 */

import { Amplify } from 'aws-amplify';
import 'aws-amplify/auth/enable-oauth-listener';

// Check if we're in local development mode
const isLocal = process.env.REACT_APP_API_ENV === 'local';
const isDev = process.env.REACT_APP_DEV_MODE === 'true';

// Only log Amplify config in local/dev — each console.log crosses the native bridge and slows startup
const _log = (isLocal || isDev) ? console.log.bind(console) : () => {};

_log('🔧 [AMPLIFY-CONFIG] Amplify Configuration:', {
  isLocal,
  isDev,
  apiEnv: process.env.REACT_APP_API_ENV,
  hasUserPoolId: !!process.env.REACT_APP_USER_POOL_ID,
  hasClientId: !!process.env.REACT_APP_USER_POOL_CLIENT_ID,
  hasCognitoDomain: !!process.env.REACT_APP_COGNITO_DOMAIN,
  region: process.env.REACT_APP_AWS_REGION,
  redirectSignIn: process.env.REACT_APP_OAUTH_REDIRECT_SIGN_IN,
  redirectSignOut: process.env.REACT_APP_OAUTH_REDIRECT_SIGN_OUT
});

if (!isLocal && !isDev) {
  // Production mode - configure Cognito (Amplify v6 format)
  const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
  const userPoolId = process.env.REACT_APP_USER_POOL_ID;
  const userPoolClientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
  
  // IMPORTANT: Use current origin to avoid Amplify v6 "InvalidOriginException"
  // Amplify v6 requires redirect URI to match the origin where OAuth is initiated
  const redirectSignIn = `${window.location.origin}/auth/callback`;
  const redirectSignOut = window.location.origin;
  
  _log('🔧 [AMPLIFY-CONFIG] Using dynamic redirect URIs based on current origin:', {
    currentOrigin: window.location.origin,
    redirectSignIn,
    redirectSignOut,
    note: 'This prevents InvalidOriginException in Amplify v6'
  });
  
  const amplifyConfig = {
    Auth: {
      Cognito: {
        userPoolId: userPoolId,
        userPoolClientId: userPoolClientId,
        loginWith: {
          oauth: {
            domain: `${cognitoDomain}.auth.${region}.amazoncognito.com`,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [redirectSignIn],
            redirectSignOut: [redirectSignOut],
            responseType: 'code',
            providers: ['Google', 'Facebook']
          }
        }
      }
    }
  };
  
  _log('📋 [AMPLIFY-CONFIG] OAuth Configuration:', {
    domain: amplifyConfig.Auth.Cognito.loginWith.oauth.domain,
    redirectSignIn: amplifyConfig.Auth.Cognito.loginWith.oauth.redirectSignIn,
    redirectSignOut: amplifyConfig.Auth.Cognito.loginWith.oauth.redirectSignOut,
    scopes: amplifyConfig.Auth.Cognito.loginWith.oauth.scopes,
    providers: amplifyConfig.Auth.Cognito.loginWith.oauth.providers,
    responseType: amplifyConfig.Auth.Cognito.loginWith.oauth.responseType
  });
  
  // Only configure if we have the required environment variables
  if (userPoolId && userPoolClientId && cognitoDomain) {
    Amplify.configure(amplifyConfig);
    _log('✅ [AMPLIFY-CONFIG] Amplify configured for production authentication');

    // Log the OAuth URLs for debugging (dev/local only)
    const googleOAuthUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/authorize?client_id=${userPoolClientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectSignIn)}&identity_provider=Google&scope=email+openid+profile`;
    const facebookOAuthUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/authorize?client_id=${userPoolClientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectSignIn)}&identity_provider=Facebook&scope=email+openid+profile`;

    _log('🔗 [AMPLIFY-CONFIG] Google OAuth URL:', googleOAuthUrl);
    _log('🔗 [AMPLIFY-CONFIG] Facebook OAuth URL:', facebookOAuthUrl);
    _log('🔗 [AMPLIFY-CONFIG] Callback URL:', redirectSignIn);
  } else {
    console.error('❌ [AMPLIFY-CONFIG] Missing required Cognito configuration!');
    console.error('Required environment variables:');
    console.error('  - REACT_APP_USER_POOL_ID:', userPoolId ? '✅' : '❌', userPoolId || 'MISSING');
    console.error('  - REACT_APP_USER_POOL_CLIENT_ID:', userPoolClientId ? '✅' : '❌', userPoolClientId ? `${userPoolClientId.substring(0, 10)}...` : 'MISSING');
    console.error('  - REACT_APP_COGNITO_DOMAIN:', cognitoDomain ? '✅' : '❌', cognitoDomain || 'MISSING');
  }
} else {
  _log('🔧 [AMPLIFY-CONFIG] Running in local development mode - using mock authentication');
  _log('🔧 [AMPLIFY-CONFIG] OAuth flows will be bypassed');
}

export default Amplify;
